import React, { useState } from "react";
import { Eye, EyeOff, User, Lock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

interface LoginCardProps {
  onLogin?: (usuario: string, password: string, remember: boolean) => Promise<void>;
}

export const LoginCard: React.FC<LoginCardProps> = ({ onLogin }) => {
  const [usuario, setUsuario] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [touched, setTouched] = useState({ usuario: false, password: false });

  const usuarioError = touched.usuario && !usuario.trim() ? "El usuario es requerido" : "";
  const passwordError =
    touched.password && !password
      ? "La contraseña es requerida"
      : touched.password && password.length < 6
      ? "Mínimo 6 caracteres"
      : "";

  const isFormValid = usuario.trim() && password && password.length >= 6;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ usuario: true, password: true });
    if (!isFormValid) return;

    setIsLoading(true);
    setError("");

    try {
      if (!onLogin) throw new Error("No hay backend conectado");
      await onLogin(usuario.trim().toUpperCase(), password, rememberMe);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Usuario o contraseña incorrectos");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md animate-fade-in-up">
      <div className="glass-card rounded-2xl p-8 md:p-10 shadow-2xl">
        <div className="text-center mb-8">
          <h1 className="font-montserrat text-2xl md:text-3xl font-bold text-card-foreground mb-2">
            Acceso al Dashboard Institucional
          </h1>
          <p className="text-muted-foreground text-sm md:text-base">Federación Nacional de Cafeteros de Colombia</p>
        </div>

        <div className="separator-elegant mb-8" />

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="usuario" className="text-sm font-medium text-card-foreground">
              Usuario
            </Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-fnc-gray" />
              <Input
                id="usuario"
                type="text"
                placeholder="DIRECTOR / PROYECTOS / FINANCIERA"
                value={usuario}
                onChange={(e) => setUsuario(e.target.value)}
                onBlur={() => setTouched((p) => ({ ...p, usuario: true }))}
                className={`pl-10 h-12 bg-muted/50 border-border input-fnc transition-all ${
                  usuarioError ? "border-destructive focus:ring-destructive/30" : ""
                }`}
              />
            </div>
            {usuarioError && <p className="text-sm text-destructive/90">{usuarioError}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium text-card-foreground">
              Contraseña
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-fnc-gray" />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setTouched((p) => ({ ...p, password: true }))}
                className={`pl-10 pr-10 h-12 bg-muted/50 border-border input-fnc transition-all ${
                  passwordError ? "border-destructive focus:ring-destructive/30" : ""
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-fnc-gray hover:text-foreground transition-colors"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {passwordError && <p className="text-sm text-destructive/90">{passwordError}</p>}
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox id="remember" checked={rememberMe} onCheckedChange={(c) => setRememberMe(Boolean(c))} />
            <Label htmlFor="remember" className="text-sm text-muted-foreground cursor-pointer">
              Recordarme
            </Label>
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
              <p className="text-sm text-destructive text-center font-medium">{error}</p>
            </div>
          )}

          <Button type="submit" disabled={isLoading} className="w-full h-12 btn-fnc-primary text-base font-semibold rounded-lg">
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Verificando...
              </>
            ) : (
              "Ingresar"
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default LoginCard;

